import { styled } from '@material-ui/core';
import { useIO, useLinked, useModel } from '@type-r/react';
import { Column, Columns, Loading } from 'lqx-react';
import * as React from 'react';
import { useEffect } from 'react';
import { Policy, PolicyBuyerSchedule } from '../../../api/policy';
import { CancelButton, ConfirmationButton } from '../../../shared';
import { UploadForm } from '../../upload-form';
import { PolicyBuyerForm } from './policy-buyer-form';
import { PolicyBuyerSearchForm } from './policy-buyer-search-form';
import { PolicyBuyersCsvParser } from './policy-buyers-csv-parser';
import { BuyerEditTitle, VerticalContainer } from './shared';

export const AddBuyersStep = ({ policy, next }: { policy: Policy, next }) => {
    const manualBuyer = useModel(PolicyBuyerSchedule);
    const $selectedSearchBuyer = useLinked(null);
    const $isManualBuyerValid = useLinked(false);

    const $file = useLinked(null);
    const $doImportCsvIO = useLinked(false);
    const isImportReady = useIO(
        async () => $doImportCsvIO.isTruthy && await importCsvFile(),
        [$doImportCsvIO.value]
    );

    useEffect(() => {
        if (!$selectedSearchBuyer.value) return;

        manualBuyer.set({
            buyerName: $selectedSearchBuyer.value.name,
            buyerDUNS: $selectedSearchBuyer.value.duns
        });
        $isManualBuyerValid.set(manualBuyer.validateBuyer());

    }, [$selectedSearchBuyer.value ? $selectedSearchBuyer.value['_changeToken'] : true]);

    const importCsvFile = async () => {
        try {
            if ($file.value) {
                const csvBuyers = await new PolicyBuyersCsvParser($file.value).parse();
                next(csvBuyers);
            }
        }
        finally {
            $doImportCsvIO.set(false);
        }
    }

    const onDownloadTemplate = () => {
        const link = document.createElement('a');
        link.href = '/assets/files/buyers_template.csv';
        link.download = 'buyers_template.csv';
        link.click();
    }

    const clearForm = () => {
        const name = manualBuyer.buyerName;
        const duns = manualBuyer.buyerDUNS;
        manualBuyer.set(manualBuyer.defaults());
        manualBuyer.buyerName = name;
        manualBuyer.buyerDUNS = duns;
        $isManualBuyerValid.set(manualBuyer.validateBuyer());
    }

    const cancel = () => {
        $selectedSearchBuyer.set(null);
        manualBuyer.set(manualBuyer.defaults());
        $isManualBuyerValid.set(manualBuyer.validateBuyer());
    }

    const addBuyer = () => {
        policy.policyBuyerSchedules.add(manualBuyer.attributes);
        cancel();
    }

    return isImportReady ?
        <>
            <Columns>
                <Column>
                    <UploadForm
                        label="Buyers"
                        $file={$file}
                        onUpload={$doImportCsvIO.true}
                        onDownloadTemplate={onDownloadTemplate} />
                </Column>
                <Column>
                    <VerticalContainer>
                        <BuyerEditTitle>Manually enter a buyer to add to insurance policy</BuyerEditTitle>
                        {!$selectedSearchBuyer.value && <PolicyBuyerSearchForm $selectedSearchBuyer={$selectedSearchBuyer} />}
                        {$selectedSearchBuyer.value &&
                            <>
                                <PolicyBuyerForm buyer={manualBuyer} $isBuyerValid={$isManualBuyerValid} />
                                <Columns>
                                    <Column><AddBuyerCancelButton onClick={clearForm}>Clear Form</AddBuyerCancelButton></Column>
                                    <Column><AddBuyerCancelButton onClick={cancel}>Cancel</AddBuyerCancelButton></Column>
                                    <Column>
                                        <AddBuyerConfirmationButton disabled={!$isManualBuyerValid.value} onClick={addBuyer}>Add Buyer</AddBuyerConfirmationButton>
                                    </Column>
                                </Columns>
                            </>}
                    </VerticalContainer>
                </Column>
            </Columns>
        </> : <Loading />
};

const AddBuyerCancelButton = styled(CancelButton)({
    marginTop: '20px',
    width: '70px'
});

const AddBuyerConfirmationButton = styled(ConfirmationButton)({
    marginTop: '20px',
    minWidth: '100px',
    width: '100px'
});
